import { useTranslation } from 'react-i18next';
import { TOptions } from 'i18next';
import * as translations from './de/index';

export type Namespaces = keyof typeof translations;
// based on: https://type-level-typescript.com/

// trims whitespaces from string literal type
type Trim<T extends string> = T extends ` ${infer Rest}`
  ? Rest extends `${infer First} `
    ? Trim<First>
    : Trim<Rest>
  : T;
// Returns the arguments of a templated string as type.
// '{{hello}} {{world}}' yields '{ hello: string | object; world: string | number }'
type TemplatedStringArgs<T extends string> = T extends `${infer _}{{${infer Start}}}${infer Rest}`
  ? { [k in Trim<Start>]: string | number } & TemplatedStringArgs<Rest>
  : // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    {};

// Depending on whether the given string <T> is a templated string, the options parameter is required,
// otherwise options are an optional argument
type GetContext<T extends string> =
  keyof TemplatedStringArgs<T> extends Record<string, unknown>
    ? [options?: TOptions]
    : [options: TOptions<TemplatedStringArgs<T>>];

type Join<First extends string | never, Second extends string, Delimiter extends string = '.'> = First extends ''
  ? Second
  : `${First}${Delimiter}${Second}`;

// returns the t function arguments for a namespace be recursively looping over its keys:
type GetKeys<
  Obj,
  Prefix extends string = '',
  Key extends string & keyof Obj = string & keyof Obj,
  Path extends string = '',
> = Key extends string
  ? Obj[Key] extends string
    ? | [Join<Prefix, Join<Path, Key>, ':'>, ...GetContext<Obj[Key]>]
      | [Join<Prefix, Join<Path, Key>, ':'>, string, ...GetContext<Obj[Key]>]
    : GetKeys<Obj[Key], Prefix, keyof Obj[Key] & string, Join<Path, Key>>
  : never;

// returns the complete arguments for the i18n t function.
type TFuncArgs<
  DefaultNS extends Namespaces,
  Namespace extends string,
> = Namespace extends `${infer NamespacePrefix}:${infer _}`
  ? NamespacePrefix extends Namespaces
    ? GetKeys<(typeof translations)[NamespacePrefix], NamespacePrefix>
    : [key: Namespace, defaultValue: string, options?: TOptions] | [key: Namespace, options?: TOptions]
  : GetKeys<(typeof translations)[DefaultNS]>;

// returns the response of the i18next useTranslation hook but with a fully typed t-function.
export function useSonTranslation<DefaultNamespace extends Namespaces>(namespace: DefaultNamespace) {
  const { t, ...useTranslationResponse } = useTranslation(namespace);
  return {
    ...useTranslationResponse,
    t: <CustomNamespace extends string>(...args: TFuncArgs<DefaultNamespace, CustomNamespace>) => {
      // @ts-expect-error -- this is just fine...
      return t(...args);
    },
  };
}
